/**
 * Cart Delivery — Fast Cutoffs + Debounced Greeting + Canonical Attributes
 * - Instant slot hiding (pre-paint CSS) including Morning/Standard cutoff
 * - ONE date attribute: Delivery-Date (hyphen)
 * - Time attributes: Delivery-Time (legacy) + Delivery Time (new)
 * - Logs AFTER cart attribute updates on every Emirate/Date/Time change
 * - Asia/Dubai time; per-emirate cutoffs; holiday blocking
 * - Calendar defaults to 3rd day (Today/Tomorrow have their own tabs)
 * - Uses existing HTML selectors (no markup changes)
 * - Enhancements: analytics decision logs, bfcache-safe (pageshow + visibilitychange), MutationObserver disconnect
 */

// GA4/GTM safety: ensure dataLayer exists globally
window.dataLayer = window.dataLayer || [];

(function () {
  if (window.CartDeliveryLoggerV3) return;
  window.CartDeliveryLoggerV3 = true;

  /* ==============================
     CONFIG — tune here
     ============================== */
  const CONFIG = {
    TZ: 'Asia/Dubai',

    // Blocked dates everywhere (Today/Tomorrow/Calendar); format: DD-MM-YYYY
    HOLIDAYS: [
      // '14-02-2026', '21-03-2026'
    ],

    // Global same-day cutoff (after this, "Today" delivery not allowed)
    GENERAL_CUTOFF: '20:00',

    // Express (Today only) window
    EXPRESS_WINDOW: { start: '10:00', end: '19:00' },

    // Today slot hide times (affects TODAY only)
    TODAY_SLOTS: {
      MORNING_HIDE_AT:  '14:00', // Morning/Standard cutoff
      EVENING_HIDE_AT:  '20:00',
      MIDNIGHT_HIDE_AT: '17:00'
    },

    // Per-emirate rules
    EMIRATES: {
      DU: { name: 'Dubai' },
      AZ: { name: 'Abu Dhabi', sundayCutoff: '12:30', otherCutoff: '16:00' },
      AI: { name: 'Al Ain',   blockAfterHour: 12 },
      SH: { name: 'Sharjah',  blockAfterHour: 14 },
      AJ: { name: 'Ajman' },
      FU: { name: 'Fujairah' },
      RK: { name: 'Ras al-Khaimah' },
      UQ: { name: 'Umm al-Quwain' }
    },

    // Calendar opens on the 3rd day (Today/Tomorrow already have buttons)
    CALENDAR_DEFAULT_OFFSET_DAYS: 2
  };

  // One-time purge list (set to empty to clear on load)
  const UNWANTED_KEYS = [
    'Delivery Date',        // ❌ keep only Delivery-Date
    'sender-phone:',        // stray colon key
    'Send as Anonymous?:'   // stray colon key
  ];

  /* ==============================
     Analytics helper
     ============================== */
  function track(event, data = {}) {
    try {
      if (Array.isArray(window.dataLayer)) {
        window.dataLayer.push({ event, ...data });
      }
    } catch (e) {}
    try {
      console.info(`[CD-analytics] ${event}`, data);
    } catch (e) {}
  }

  /* ==============================
     Tiny helpers
     ============================== */
  const hasMomentTZ = () => (typeof moment !== 'undefined' && typeof moment.tz === 'function');

  function dubaiNowFallbackDate() {
    // Force "now" in Asia/Dubai using Intl even if moment-timezone is missing
    try {
      return new Date(new Date().toLocaleString('en-US', { timeZone: CONFIG.TZ }));
    } catch {
      return new Date(); // last resort: system local
    }
  }

  (function assertTimezone(){
    if (!hasMomentTZ()) {
      console.warn('[CartDelivery][TZ] moment-timezone not detected. Using Intl/UTC+4 fallback for Asia/Dubai.');
    }
  })();

  // "Now" in Dubai as a moment-like object
  const nowDubai = () => {
    if (hasMomentTZ()) return moment.tz(CONFIG.TZ);
    // Wrap Dubai-local Date in moment so .format() works consistently
    return moment(dubaiNowFallbackDate());
  };

  // Format helpers — if created from a Dubai-local Date, plain .format() is correct
  const fmtDMY        = (m) => (hasMomentTZ() ? m.tz(CONFIG.TZ) : m).format('DD-MM-YYYY');
  const fmtDMYSlashes = (m) => (hasMomentTZ() ? m.tz(CONFIG.TZ) : m).format('DD/MM/YYYY');

  // Parse "DD-MM-YYYY" as a Dubai-local date even without moment.tz
  const parseDMY = (s) => {
    if (hasMomentTZ()) return moment.tz(s, 'DD-MM-YYYY', true, CONFIG.TZ);
    const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(String(s) || '');
    if (!m) return moment.invalid();
    const dd = parseInt(m[1], 10), MM = parseInt(m[2], 10) - 1, yyyy = parseInt(m[3], 10);
    // UAE is fixed UTC+4 all year; build a Date representing Dubai midnight
    const utcMs   = Date.UTC(yyyy, MM, dd, 0, 0, 0);
    const dubaiMs = utcMs + (4 * 60 * 60 * 1000); // +04:00
    return moment(new Date(dubaiMs));
  };

  const sameDay = (a, b) => fmtDMY(a) === fmtDMY(b);
  const timeGE  = (a, b) => a >= b; // strings "HH:mm"
  const timeLT  = (a, b) => a <  b;

  const q  = (sel, root=document) => root.querySelector(sel);
  const qa = (sel, root=document) => Array.from(root.querySelectorAll(sel));

  const emirateName = code => (CONFIG.EMIRATES[code]?.name || 'Dubai');
  const selectedEmirateCode = () => (q('#Emirate')?.value || 'DU');

  // Accepts both .morning-delivery and .standard-delivery as "Morning/Standard"
  const slots = () => ({
    morning:  q('.morning-delivery') || q('.standard-delivery'),
    evening:  q('.evening-delivery'),
    express:  q('.today-only') || q('.express'),
    midnight: q('.midnight-option')
  });

  const isHoliday = dmy => CONFIG.HOLIDAYS.includes(dmy);

  // ===== Debounce helper (used for greeting/phone inputs) =====
  function debounce(fn, wait = 500) {
    let t, lastArgs, lastThis;
    const wrapped = function(...args) {
      lastArgs = args; lastThis = this;
      clearTimeout(t);
      t = setTimeout(() => { t = null; fn.apply(lastThis, lastArgs); }, wait);
    };
    wrapped.cancel = () => { clearTimeout(t); t = null; };
    wrapped.flush  = () => { if (t) { clearTimeout(t); t = null; fn.apply(lastThis, lastArgs); } };
    return wrapped;
  }

  /* ==============================
     PRE-PAINT FAST HIDE (Morning/Standard etc.) — uses CONFIG
     ============================== */
  (function fastHideSlotsPrePaint() {
    // Dubai time via Intl (works pre-moment)
    function dubaiHHmm() {
      try {
        const d = new Date(new Date().toLocaleString('en-US', { timeZone: CONFIG.TZ }));
        const pad = n => String(n).padStart(2,'0');
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      } catch {
        const d = new Date();
        const pad = n => String(n).padStart(2,'0');
        return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
      }
    }
    const t = dubaiHHmm();
    const ge = (a,b)=>a>=b, lt=(a,b)=>a<b;

    // Read current emirate if available, default DU instantly
    const emSel = document.querySelector('#Emirate');
    const code = emSel?.value || 'DU';

    // Quick "today blocked" heuristic aligned with main logic
    let todayBlocked = false;
    // Global cutoff
    if (ge(t, CONFIG.GENERAL_CUTOFF)) todayBlocked = true;
    // Al Ain: block after 10:00
    if (code === 'AI' && parseInt(t.slice(0,2),10) >= (CONFIG.EMIRATES.AI.blockAfterHour ?? 24)) todayBlocked = true;
    // Sharjah: block after 12:00
    if (code === 'SH' && parseInt(t.slice(0,2),10) >= (CONFIG.EMIRATES.SH.blockAfterHour ?? 24)) todayBlocked = true;
    // FU/AJ/RK/UQ: today blocked
    if (['FU','AJ','RK','UQ'].includes(code)) todayBlocked = true;

    let css = '';
    if (todayBlocked) {
      css += `#delivery-option{display:none!important}`;
      track('cd_today_blocked_prepaint', { reason: 'heuristic', emirate: code, time: t });
    } else {
      // Morning (Standard) cutoff
      if (ge(t, CONFIG.TODAY_SLOTS.MORNING_HIDE_AT))  css += `.morning-delivery,.standard-delivery{display:none!important}`;
      // Evening cutoff
      if (ge(t, CONFIG.TODAY_SLOTS.EVENING_HIDE_AT))  css += `.evening-delivery{display:none!important}`;
      // Midnight cutoff
      if (ge(t, CONFIG.TODAY_SLOTS.MIDNIGHT_HIDE_AT)) css += `.midnight-option{display:none!important}`;
      // Express is today-only and time-window bound
      const expressWithin = ge(t, CONFIG.EXPRESS_WINDOW.start) && lt(t, CONFIG.EXPRESS_WINDOW.end);
      if (!expressWithin) css += `.today-only,.express{display:none!important}`;
    }

    if (css) {
      const tag = document.createElement('style');
      tag.id = 'fast-slot-hide';
      tag.textContent = css;
      document.head.appendChild(tag);
    }
  })();

  /* ==============================
   Coalesced (single-flight) cart updates
   - Merges rapid calls into one network request.
   - Guarantees at most one in-flight POST.
   - Exposes .flush() to force-send pending attrs (used on visibility/page hide).
   ============================== */
  let __attrsPending = null;     // merged payload waiting to be sent
  let __attrsSending = null;     // the current in-flight Promise

  function mergeAttrs(base, next) {
    return Object.assign({}, base || {}, next || {});
  }

  function updateAttrsCoalesced(obj) {
    __attrsPending = mergeAttrs(__attrsPending, obj);

    // If a send loop is already running, just piggyback by merging.
    if (__attrsSending) return __attrsSending;

    // Start a send loop that drains the pending payloads sequentially.
    __attrsSending = (async function sendLoop() {
      try {
        while (__attrsPending) {
          const payload = __attrsPending;
          __attrsPending = null;               // clear current batch
          await updateAttrs(payload);          // uses your existing updater
        }
      } finally {
        __attrsSending = null;                 // mark channel as idle
      }
    })();

    return __attrsSending;
  }

  // Force-send anything pending (used when page goes to background)
  updateAttrsCoalesced.flush = async function flushCoalesced() {
    if (__attrsSending) {
      try { await __attrsSending; } catch (e) {}
      return;
    }
    if (!__attrsPending) return;               // nothing to do
    __attrsSending = (async function singleSend() {
      try {
        const payload = __attrsPending;
        __attrsPending = null;
        await updateAttrs(payload);
      } finally {
        __attrsSending = null;
      }
    })();
    try { await __attrsSending; } catch (e) {}
  };


  /* ==============================
     Cart attribute I/O + logging
     ============================== */
  let updating = false;

  async function updateAttrs(obj) {
    const fd = new FormData();
    Object.entries(obj).forEach(([k, v]) => fd.append(`attributes[${k}]`, v == null ? '' : v));

    console.groupCollapsed('[CartDelivery] Cart Attribute Update');
    Object.entries(obj).forEach(([k, v]) => console.log(`${k}:`, v));
    console.groupEnd();

    while (updating) await new Promise(r => setTimeout(r, 80));
    updating = true;
    try {
      const res = await fetch('/cart/update.js', { method: 'POST', body: fd });
      let json = {};
      try { json = await res.json(); } catch { /* non-JSON edge */ }
      if (!res.ok) console.warn('[CartDelivery] Update non-OK:', res.status, json);
      else console.log('[CartDelivery] Server attributes echo:', json.attributes);
      return json;
    } catch (e) {
      console.warn('[CartDelivery] Update failed:', e);
    } finally {
      updating = false;
    }
  }

  async function purgeUnwantedAttributesOnce() {
    if (!UNWANTED_KEYS.length) return;
    const payload = {};
    UNWANTED_KEYS.forEach(k => payload[k] = '');
    await updateAttrs(payload);
    console.log('[CartDelivery] Purged unwanted attributes:', UNWANTED_KEYS.join(', '));
  }

  /* ==============================
     UI helpers (Today vs Future)
     ============================== */
  function show(el){ if(el){ el.style.display='block'; el.classList.remove('hidetimerfirst'); } }
  function hide(el){ if(el){ el.style.display='none';  el.classList.add('hidetimerfirst');  } }

  function applyTodayCutoffs() {
    const { morning, evening, express, midnight } = slots();
    const t = nowDubai().format('HH:mm');

    // Morning/Standard cutoff (works at/after the time)
    if (morning)  (timeGE(t, CONFIG.TODAY_SLOTS.MORNING_HIDE_AT)  ? (hide(morning), track('cd_slot_hide', { slot:'morning', at:t }))  : show(morning));
    if (evening)  (timeGE(t, CONFIG.TODAY_SLOTS.EVENING_HIDE_AT)  ? (hide(evening), track('cd_slot_hide', { slot:'evening', at:t }))  : show(evening));
    if (midnight) (timeGE(t, CONFIG.TODAY_SLOTS.MIDNIGHT_HIDE_AT) ? (hide(midnight), track('cd_slot_hide', { slot:'midnight', at:t })) : show(midnight));

    if (express) {
      const within = timeGE(t, CONFIG.EXPRESS_WINDOW.start) && timeLT(t, CONFIG.EXPRESS_WINDOW.end);
      within ? show(express) : (hide(express), track('cd_slot_hide', { slot:'express', at:t }));
    }
  }

  function applyFutureSlots() {
    const { morning, evening, express, midnight } = slots();
    if (morning)  show(morning);   // Morning/Standard must be visible for future dates
    if (evening)  show(evening);
    if (midnight) show(midnight);
    if (express) { hide(express); express.classList.add('hidetimerfirst'); } // Express is Today-only
  }

  function isTodayBlockedByEmirate(code, nowM) {
    const t = nowM.format('HH:mm');
    const h = parseInt(nowM.format('HH'), 10);

    // Global today cutoff
    if (timeGE(t, CONFIG.GENERAL_CUTOFF)) { track('cd_today_blocked', { reason:'general_cutoff', at:t }); return true; }

    // Abu Dhabi (Sunday vs other cutoff)
    if (code === 'AZ') {
      const isSunday = nowM.format('dddd') === 'Sunday';
      const cutoff = isSunday ? CONFIG.EMIRATES.AZ.sundayCutoff : CONFIG.EMIRATES.AZ.otherCutoff;
      if (cutoff && timeGE(t, cutoff)) { track('cd_today_blocked', { reason:'abu_dhabi_cutoff', isSunday, cutoff, at:t }); return true; }
    }

    // Al Ain: block after 10:00
    if (code === 'AI' && h >= (CONFIG.EMIRATES.AI.blockAfterHour ?? 24)) { track('cd_today_blocked', { reason:'al_ain_hour', at:t }); return true; }

    // Sharjah: block after 12:00
    if (code === 'SH' && h >= (CONFIG.EMIRATES.SH.blockAfterHour ?? 24)) { track('cd_today_blocked', { reason:'sharjah_hour', at:t }); return true; }

    // FU/AJ/RK/UQ: next-day only ⇒ Today blocked
    if (['FU','AJ','RK','UQ'].includes(code)) { track('cd_today_blocked', { reason:'other_emirate_policy', emirate:code }); return true; }

    return false;
  }

  // ---- Next-available-day helpers (Tomorrow preferred; else next non-holiday)
  function nextNonHolidayMoment(startM) {
    let m = startM.clone();
    for (let i=0;i<31;i++){
      const dmy = fmtDMY(m);
      if (!isHoliday(dmy)) return m;
      m = m.add(1,'day');
    }
    return startM.clone().add(1,'day'); // fallback
  }

  function computeNextActiveDate() {
    const now = nowDubai();
    const code = selectedEmirateCode();
    const todayBlocked = isTodayBlockedByEmirate(code, now);
    const todayIsHoliday = isHoliday(fmtDMY(now));

    if (!todayBlocked && !todayIsHoliday) return { m: now, label: 'Today' };

    const tomorrow = now.clone().add(1,'day');
    if (!isHoliday(fmtDMY(tomorrow))) {
      track('cd_autoselect_date', { to:'tomorrow', reason: todayBlocked ? 'blocked_today' : 'holiday_today' });
      return { m: tomorrow, label: 'Tomorrow' };
    }

    const nextWork = nextNonHolidayMoment(tomorrow);
    track('cd_autoselect_date', { to:'calendar_future', reason:'tomorrow_holiday', dmy: fmtDMY(nextWork) });
    return { m: nextWork, label: 'Selected' }; // calendar date
  }

  function disableTodayTab() {
    const todayA = q('.today-date');
    if (!todayA) return;
    todayA.setAttribute('aria-disabled','true');
    todayA.setAttribute('tabindex','-1');
    todayA.classList.add('disabled');
    todayA.style.pointerEvents = 'none';
    todayA.style.opacity = '0.55';
    todayA.title = 'Today delivery unavailable now';
  }

  /* ==============================
     Selection -> log (after updates)
     ============================== */
  function selectedTimeLabel() {
    const r = q('input[name="attributes[Delivery Time]"]:checked');
    if (!r) return '';
    const txt = r.closest('.form-check')?.querySelector('label')?.textContent?.trim();
    if (txt && /express/i.test(txt)) return 'Express';
    return txt || r.value || '';
  }

  function logCurrentState(label) {
    const code = selectedEmirateCode();
    const emirate = emirateName(code);
    const dmy = (q('#datepicker')?.value || '').trim();           // DD-MM-YYYY
    const dateNice = dmy.replace(/-/g, '/');                      // DD/MM/YYYY for log
    const timeText = selectedTimeLabel();
    const suffix = timeText ? ` -> ${timeText}` : '';
    console.log(`Emirates: ${emirate} -> ${label} [${dateNice}]${suffix}`);
  }

  /* ==============================
     Datepicker + tabs
     ============================== */
  function installDatepickerGuards() {
    if (typeof $ === 'undefined' || !$.datepicker) return;
    $('#datepicker').datepicker('option', 'beforeShowDay', function(dateJS){
      const m = hasMomentTZ() ? moment(dateJS).tz(CONFIG.TZ) : moment(dateJS);
      const dmy = fmtDMY(m);
      const today = nowDubai();
      const tomorrow = nowDubai().add(1,'day');

      if (dmy === fmtDMY(today) || dmy === fmtDMY(tomorrow)) {
        return [false, 'disabled-date', 'Use the Today/Tomorrow buttons'];
      }
      if (isHoliday(dmy)) {
        return [false, 'holiday-date', 'Unavailable (Holiday)'];
      }
      return [true, ''];
    });
  }

  function initDatepickerAndTabs() {
    const dp = q('#datepicker');
    if (!dp || typeof $ === 'undefined' || !$.datepicker) {
      console.warn('[CartDelivery] Datepicker not found/loaded.');
      return;
    }

    $('#datepicker').datepicker({
      dateFormat: 'dd-mm-yy',
      minDate: 0,
      maxDate: '+30D',
      onSelect: async function(dateText, inst) {
        const dd = String(inst.selectedDay).padStart(2,'0');
        const mm = String(inst.selectedMonth + 1).padStart(2,'0');
        const yyyy = String(inst.selectedYear);
        const picked = `${dd}-${mm}-${yyyy}`;
        await syncDateAndLog(picked);
      }
    });
    installDatepickerGuards();

    // TODAY tab — guard against cutoff/blocked/holiday
    q('.today-date')?.addEventListener('click', async (e) => {
      e.preventDefault();
      const now = nowDubai();
      const code = selectedEmirateCode();

      if (isTodayBlockedByEmirate(code, now) || isHoliday(fmtDMY(now))) {
        disableTodayTab();
        // Prefer Tomorrow; else jump to next non-holiday calendar date
        const tomorrow = now.clone().add(1,'day');
        const nextM = isHoliday(fmtDMY(tomorrow)) ? nextNonHolidayMoment(tomorrow) : tomorrow;
        const dmy = fmtDMY(nextM);

        // If it's exactly tomorrow, visually toggle Tomorrow tab; else Calendar tab
        if (sameDay(nextM, now.clone().add(1,'day'))) {
          q('.tab-a.active-a')?.classList.remove('active-a');
          q('.tomorrow-date')?.classList.add('active-a');
          track('cd_click_today_blocked_redirect', { to:'tomorrow' });
        } else {
          q('.tab-a.active-a')?.classList.remove('active-a');
          q('.view-calendar')?.classList.add('active-a');
          track('cd_click_today_blocked_redirect', { to:'calendar', dmy });
        }

        $('#datepicker').datepicker('setDate', nextM.toDate());
        if (dp) dp.value = dmy;
        await syncDateAndLog(dmy);
        return;
      }

      // Allowed: stick to Today
      const dmy = fmtDMY(now);
      $('#datepicker').datepicker('setDate', now.toDate());
      if (dp) dp.value = dmy;
      await syncDateAndLog(dmy);
    });

    q('.tomorrow-date')?.addEventListener('click', async (e) => {
      e.preventDefault();
      const t = nowDubai().add(1,'day');
      const dmy = fmtDMY(t);
      $('#datepicker').datepicker('setDate', t.toDate());
      if (dp) dp.value = dmy;
      await syncDateAndLog(dmy);
    });

    q('.view-calendar')?.addEventListener('click', async (e) => {
      e.preventDefault();
      const t = nowDubai().add(CONFIG.CALENDAR_DEFAULT_OFFSET_DAYS, 'day');
      const dmy = fmtDMY(t);
      setTimeout(async () => {
        $('#datepicker').datepicker('setDate', t.toDate());
        if (dp) dp.value = dmy;
        await syncDateAndLog(dmy);
      }, 200);
    });
  }

  /* ==============================
     Emirate / Time / Phone / Greeting
     ============================== */
  async function syncEmirateAndLog() {
    await updateAttrs({ 'Emirate': selectedEmirateCode() });
    const dmy = q('#datepicker')?.value || fmtDMY(nowDubai());
    applyDateChoice(dmy);
    autoPickFirstVisibleTime();
    const todayDmy = fmtDMY(nowDubai());
    const tomorrowDmy = fmtDMY(nowDubai().add(1,'day'));
    const label = dmy === todayDmy ? 'Today' : (dmy === tomorrowDmy ? 'Tomorrow' : 'Selected');
    logCurrentState(label);
  }

  async function syncDateAndLog(dmy) {
    await updateAttrs({ 'Delivery-Date': dmy });
    applyDateChoice(dmy);
    autoPickFirstVisibleTime();
    const todayDmy = fmtDMY(nowDubai());
    const tomorrowDmy = fmtDMY(nowDubai().add(1,'day'));
    const label = dmy === todayDmy ? 'Today' : (dmy === tomorrowDmy ? 'Tomorrow' : 'Selected');
    logCurrentState(label);
  }

  async function syncTimeAndLog() {
    const r = q('input[name="attributes[Delivery Time]"]:checked');
    if (!r) return;
    const v = r.value || '';
    await updateAttrs({ 'Delivery-Time': v, 'Delivery Time': v });
    const dmy = q('#datepicker')?.value || fmtDMY(nowDubai());
    const todayDmy = fmtDMY(nowDubai());
    const tomorrowDmy = fmtDMY(nowDubai().add(1,'day'));
    const label = dmy === todayDmy ? 'Today' : (dmy === tomorrowDmy ? 'Tomorrow' : 'Selected');
    logCurrentState(label);
  }

  function initEmirate() {
    const el = q('#Emirate');
    if (!el) return;
    el.addEventListener('change', syncEmirateAndLog);
  }

  function initTimeRadios() {
    qa('input[name="attributes[Delivery Time]"]').forEach(radio => {
      radio.addEventListener('change', syncTimeAndLog);
    });
  }

  function initPhone() {
    const input = q('#sender-phone');
    if (!input) return;
    let iti = null;
    if (typeof window.intlTelInput === 'function') {
      iti = window.intlTelInput(input, {
        initialCountry: "ae",
        separateDialCode: true,
        preferredCountries: ["ae","sa","kw","qa","bh","om"],
        utilsScript: "https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.3/js/utils.js"
      });
    }
    const hidden = q('.receivers_phone_country_code');
    const sync = async () => {
      let dial = '';
      if (iti) {
        const d = iti.getSelectedCountryData();
        dial = d?.dialCode ? `+${d.dialCode}` : '';
        if (hidden) hidden.value = dial;
      }
      //await updateAttrs({ 'sender-phone': input.value || '', 'receivers_phone_number_code': dial });
      updateAttrsCoalesced({ 'sender-phone': input.value || '', 'receivers_phone_number_code': dial });
      console.log(`[CartDelivery] Phone set: ${dial} ${input.value || ''}`);
    };
    const syncDebounced = debounce(sync, 300);
    input.addEventListener('input', syncDebounced);
  }

  // ===== Debounced Greeting (500ms) =====
  function initGreeting() {
    const el = q('#giftMessage')
      || q('textarea[name="attributes[Card Message]"]')
      || q('textarea[name="attributes[Greeting Message]"]')
      || q('textarea[name="attributes[Message]"]');
    if (!el) return;

    const sync = async () => {
      //await updateAttrs({ 'Card Message': (el.value || '').trim() });
      updateAttrsCoalesced({ 'Card Message': (el.value || '').trim() }); // ⬅️ coalesced
      console.log('[CartDelivery] Card Message updated (debounced).');
    };

    const syncDebounced = debounce(sync, 500);
    window.__greetingSync = syncDebounced; // allow cancel from "No Message"
    el.addEventListener('input', syncDebounced);
  }

  /* ==============================
     Date application + auto-pick
     ============================== */
  function autoPickFirstVisibleTime() {
    const containers = ['.dubai-options','.abudhabi-options','.other-emirates-options']
      .map(s => q(s)).filter(Boolean).filter(el => !el.classList.contains('d-none'));
    for (const c of containers) {
      const opt = qa('.form-check', c).find(o => o.style.display !== 'none' && !o.classList.contains('hidetimerfirst'));
      if (opt) {
        const inp = opt.querySelector('input[type="radio"]');
        if (inp) { inp.checked = true; inp.dispatchEvent(new Event('change', { bubbles:true })); }
        return;
      }
    }
  }

  function applyDateChoice(dmy) {
    const picked   = parseDMY(dmy);
    const emirate  = selectedEmirateCode();
    const today    = nowDubai();
    const ctr      = q('#delivery-option');

    if (isHoliday(dmy)) {
      if (ctr) ctr.style.display = 'none';
      track('cd_time_hidden', { reason:'holiday', dmy });
      return;
    }

    if (picked.isValid && !picked.isValid()) {
      if (ctr) ctr.style.display = 'block';
      applyFutureSlots();
      return;
    }

    if (sameDay(picked, today)) {
      const blocked = isTodayBlockedByEmirate(emirate, today);
      if (ctr) ctr.style.display = blocked ? 'none' : 'block';
      if (blocked) track('cd_time_hidden', { reason:'today_blocked', emirate, at: today.format('HH:mm') });
      if (!blocked) applyTodayCutoffs(); // Morning/Standard enforcement
      return;
    }

    // Tomorrow or any future date
    if (ctr) ctr.style.display = 'block';
    applyFutureSlots();
  }

  // Re-apply cutoffs instantly if the container gets injected later (disconnect after first stabilization)
  (function observeDeliveryOptions(){
    const mo = new MutationObserver(() => {
      const ctr = document.querySelector('#delivery-option');
      if (!ctr) return;
      const d = (document.querySelector('#datepicker')?.value) || '';
      if (d) {
        applyDateChoice(d);
        if (!document.querySelector('input[name="attributes[Delivery Time]"]:checked')) {
          autoPickFirstVisibleTime();
        }
      }
      const fastTag = document.getElementById('fast-slot-hide');
      if (fastTag) fastTag.remove(); // cleanup pre-paint CSS once we control UI

      // Disconnect after first successful stabilization to avoid long-lived observers
      mo.disconnect();
      track('cd_observer_disconnected', { reason:'stabilized' });
    });
    mo.observe(document.documentElement, { childList:true, subtree:true });
  })();

  // === Validate & Correct (non-destructive) ===
  function isTimeOptionVisible(val) {
    var radios = document.querySelectorAll('input[name="attributes[Delivery Time]"]');
    for (var i = 0; i < radios.length; i++) {
      if (radios[i].value === val) {
        var opt = radios[i].closest('.form-check');
        return !!(opt && opt.style.display !== 'none' && !opt.classList.contains('hidetimerfirst') && opt.offsetParent !== null);
      }
    }
    return false;
  }

  async function validateAndCorrectDeliverySelection() {
    // 1) Ensure Emirate sane default
    var code = selectedEmirateCode();
    if (!code) {
      var emSel = document.querySelector('#Emirate');
      if (emSel) emSel.value = 'DU';
      await updateAttrs({ 'Emirate': 'DU' });
      track('cd_fix', { field:'Emirate', to:'DU' });
    }

    // 2) Validate date (missing/invalid/holiday/today-blocked)
    var dp = document.querySelector('#datepicker');
    var hasDate = dp && dp.value;
    var now = nowDubai();
    var pickedM = hasDate ? parseDMY(dp.value) : null;

    var needRecompute =
      !hasDate ||
      (pickedM && pickedM.isValid && !pickedM.isValid()) ||
      (pickedM && isHoliday(fmtDMY(pickedM))) ||
      (pickedM && sameDay(pickedM, now) && isTodayBlockedByEmirate(selectedEmirateCode(), now));

    if (needRecompute) {
      var nextChoice = computeNextActiveDate(); // prefers Tomorrow, else next non-holiday
      var nextDmy = fmtDMY(nextChoice.m);
      if (typeof $ !== 'undefined' && $.datepicker) {
        $('#datepicker').datepicker('setDate', nextChoice.m.toDate());
      }
      if (dp) dp.value = nextDmy;
      await updateAttrs({ 'Delivery-Date': nextDmy });
      applyDateChoice(nextDmy);
      track('cd_fix', { field:'Delivery-Date', to: nextDmy });
    } else {
      applyDateChoice(dp.value);
    }

    // 3) Validate time (if selected is hidden, auto-pick first visible)
    var checked = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
    if (!checked || !isTimeOptionVisible(checked.value)) {
      if (checked) checked.checked = false;
      autoPickFirstVisibleTime(); // will trigger syncTimeAndLog via change
      track('cd_fix', { field:'Delivery Time', reason:'hidden_or_missing' });
    }
  }

  // Smarter ticker: snap to next cutoff, then 60s
  function startTodayTicker() {
    function nextCutoffDelayMs() {
      const now = nowDubai();
      const targets = [
        CONFIG.TODAY_SLOTS.MORNING_HIDE_AT,
        CONFIG.TODAY_SLOTS.MIDNIGHT_HIDE_AT,
        CONFIG.TODAY_SLOTS.EVENING_HIDE_AT,
        CONFIG.EXPRESS_WINDOW.start,
        CONFIG.EXPRESS_WINDOW.end,
        CONFIG.GENERAL_CUTOFF
      ].map(s=> {
        const [H,M]=s.split(':').map(Number);
        const t = now.clone().hour(H).minute(M).second(0);
        if (t.isBefore(now)) t.add(1,'day');
        return t;
      });
      const soonest = targets.sort((a,b)=>a.valueOf()-b.valueOf())[0];
      return Math.min( (soonest.valueOf() - now.valueOf()), 5*60*1000 ); // cap at 5min
    }

    const applyIfToday = () => {
      const v = q('#datepicker')?.value;
      if (!v) return;

      // If general cutoff passed, disable Today tab immediately
      const tNow = nowDubai().format('HH:mm');
      if (timeGE(tNow, CONFIG.GENERAL_CUTOFF)) {
        disableTodayTab();
      }

      const dm = parseDMY(v);
      if (dm?.isValid?.() && sameDay(dm, nowDubai())) applyTodayCutoffs();
    };

    setTimeout(() => {
      applyIfToday();
      setInterval(applyIfToday, 60000);
    }, nextCutoffDelayMs());
  }

  /* ==============================
     Boot — UI first, then network
     ============================== */
  document.addEventListener('DOMContentLoaded', async () => {
    await purgeUnwantedAttributesOnce();

    const emSel = q('#Emirate');
    if (emSel && !emSel.value) emSel.value = 'DU';

    // Init inputs
    initPhone();
    initGreeting();           // debounced 500ms
    initTimeRadios();
    initEmirate();
    initDatepickerAndTabs();
    setupNoMessageOption();

    // Decide initial date: if Today blocked/holiday or after cutoff, move to next active day
    const choice = computeNextActiveDate();
    const chosenM = choice.m;
    const chosenDmy = fmtDMY(chosenM);

    // Disable Today tab up-front if blocked
    const nowT = nowDubai().format('HH:mm');
    if (timeGE(nowT, CONFIG.GENERAL_CUTOFF) || isTodayBlockedByEmirate(selectedEmirateCode(), nowDubai()) || isHoliday(fmtDMY(nowDubai()))) {
      disableTodayTab();
    }

    if (typeof $ !== 'undefined' && $.datepicker) {
      $('#datepicker').datepicker('setDate', chosenM.toDate());
    }
    const dp = q('#datepicker'); if (dp) dp.value = chosenDmy;
    applyDateChoice(chosenDmy);
    autoPickFirstVisibleTime();

    // Non-destructive sanity pass (repairs only if needed)
    await validateAndCorrectDeliverySelection();

    // Visually toggle the appropriate tab
    const todayM = nowDubai();
    const isToday = sameDay(chosenM, todayM);
    const isTomorrow = sameDay(chosenM, todayM.clone().add(1,'day'));
    q('.tab-a.active-a')?.classList.remove('active-a');
    if (isToday) {
      q('.today-date')?.classList.add('active-a');
    } else if (isTomorrow) {
      q('.tomorrow-date')?.classList.add('active-a');
    } else {
      q('.view-calendar')?.classList.add('active-a');
    }

    // Update attributes (non-blocking)
    void updateAttrs({ 'Emirate': selectedEmirateCode(), 'Delivery-Date': chosenDmy });

    // Remove pre-paint CSS once our logic is active
    const fastTag = document.getElementById('fast-slot-hide');
    if (fastTag) fastTag.remove();

    // Initial log line (if time radio is pre-selected)
    const todayDmy = fmtDMY(nowDubai());
    if ((q('input[name="attributes[Delivery Time]"]:checked')) && chosenDmy === todayDmy) {
      logCurrentState('Today');
    }

    // Start ticker for minute-by-minute changes (snapped to next cutoff)
    startTodayTicker();

    // --- GA4: cart_view (Realtime/DebugView friendly) ---
    try {
      var emirate   = (document.querySelector('#Emirate') && document.querySelector('#Emirate').value) || '';
      var dateVal   = (document.querySelector('#datepicker') && document.querySelector('#datepicker').value) || '';
      var timeRadio = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
      var timeVal   = timeRadio ? timeRadio.value : '';
      var itemsCnt  = (document.querySelectorAll('.cart-item') || []).length;
      var subtotalT = (document.querySelector('.totals__subtotal-value') || {}).innerText || '';

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cart_view',
        page_type: 'cart',
        cart_items_count: itemsCnt,
        cart_subtotal_text: subtotalT,
        emirate: emirate,
        delivery_date: dateVal,
        delivery_time: timeVal
      });
    } catch(e) {}
    
    // --- GA4: validation pass/fail when Checkout is clicked ---
    (function wireValidationEvents(){
      function gaCartValidationResult(ok, reasonsArr) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: ok ? 'cart_validation_passed' : 'cart_validation_failed',
          validation_ok: !!ok,
          validation_reasons: (reasonsArr && reasonsArr.length) ? reasonsArr.join('|') : ''
        });
      }

      var checkoutBtn = document.getElementById('checkout');
      if (!checkoutBtn) return;

      checkoutBtn.addEventListener('click', function(){
        try {
          var reasons = [];
          var emirate = (document.querySelector('#Emirate')||{}).value || '';
          var timeSel = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
          var phone   = (document.querySelector('#sender-phone')||{}).value || '';

          if (!emirate) reasons.push('no_emirate');
          if (!timeSel) reasons.push('no_time');
          if (!phone || !String(phone).trim()) reasons.push('no_phone');

          if (reasons.length) {
            gaCartValidationResult(false, reasons);  // ❌ failed
          } else {
            gaCartValidationResult(true, []);        // ✅ passed
          }
        } catch(e){}
        // Do not prevent default here; other code handles UX/flow
      });
    })();

    // --- GA4: begin_checkout on "Continue" (.save-checkout) + flush coalesced updates ---
    (function wireBeginCheckoutEvent(){
      var btn = document.querySelector('.save-checkout');
      if (!btn) return;

      btn.addEventListener('click', function(){
        try {
          var emirate   = (document.querySelector('#Emirate') && document.querySelector('#Emirate').value) || '';
          var dateVal   = (document.querySelector('#datepicker') && document.querySelector('#datepicker').value) || '';
          var r         = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
          var timeVal   = r ? r.value : '';
          var itemsCnt  = (document.querySelectorAll('.cart-item') || []).length;

          window.dataLayer = window.dataLayer || [];
          window.dataLayer.push({
            event: 'begin_checkoutv1',
            page_type: 'cart',
            emirate: emirate,
            delivery_date: dateVal,
            delivery_time: timeVal,
            cart_items_count: itemsCnt
          });
        } catch(e) {}

        // Ensure any pending phone/greeting updates are sent before navigation (iOS safe)
        if (typeof updateAttrsCoalesced !== 'undefined' && updateAttrsCoalesced.flush) {
          updateAttrsCoalesced.flush();
        }
      });
    })();

    // --- GA4: capture JS errors on the cart page (optional) ---
    (function wireErrorTracking(){
      try {
        window.addEventListener('error', function(ev){
          try {
            var msg = (ev && ev.message) ? String(ev.message).slice(0,180) : '';
            var src = (ev && ev.filename) || '';
            var ln  = (ev && ev.lineno) || 0;
            var cn  = (ev && ev.colno) || 0;
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'cart_js_error',
              message: msg,
              source: src,
              line: ln,
              col: cn
            });
          } catch(e){}
        });

        window.addEventListener('unhandledrejection', function(ev){
          try {
            var r = ev && ev.reason;
            var msg = '';
            if (r && typeof r === 'object') {
              msg = r.message || JSON.stringify(r);
            } else {
              msg = String(r || '');
            }
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
              event: 'cart_js_error',
              message: String(msg).slice(0,180),
              source: 'unhandledrejection'
            });
          } catch(e){}
        });
      } catch(e){}
    })();

    console.log('[CartDelivery] Ready.');
  });

  /* ==============================
     iPhone/Safari back-button safety + page visibility
     ============================== */
  window.addEventListener('pageshow', function () {
    var dp = document.querySelector('#datepicker');
    if (dp && dp.value) {
      applyDateChoice(dp.value);
    }
    var checked = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
    if (!checked || !isTimeOptionVisible(checked.value)) {
      autoPickFirstVisibleTime();
    }
    var now = nowDubai();
    var todayBlocked = isTodayBlockedByEmirate(selectedEmirateCode(), now) || isHoliday(fmtDMY(now));
    if (todayBlocked) {
      disableTodayTab?.();
    }
    Promise.resolve().then(validateAndCorrectDeliverySelection);
    track('cd_pageshow_revalidate', {});
  });

  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') {
      validateAndCorrectDeliverySelection();
      track('cd_visibility_revalidate', {});
    } else if (document.visibilityState === 'hidden') {
      // ⬇️ Flush any coalesced cart attribute updates before the page backgrounds
      updateAttrsCoalesced.flush();
    }
  });

  // iOS Safari often fires pagehide when navigating away; flush again as a safety net
  window.addEventListener('pagehide', function () {
    updateAttrsCoalesced.flush();
  });

  /* ==============================
     Centralized change logger (fallback)
     ============================== */
  (function attachUnifiedSelectionLogger() {
    if (window.__deliveryLoggerAttached__) return;
    window.__deliveryLoggerAttached__ = true;

    function dubaiDmy(offsetDays=0) {
      if (typeof moment !== 'undefined' && moment.tz) {
        return moment.tz(CONFIG.TZ).add(offsetDays, 'day').format('DD-MM-YYYY');
      }
      const now = new Date(new Date().toLocaleString('en', { timeZone: CONFIG.TZ }));
      now.setDate(now.getDate() + offsetDays);
      const d = String(now.getDate()).padStart(2, '0');
      const m = String(now.getMonth() + 1).padStart(2, '0');
      const y = now.getFullYear();
      return `${d}-${m}-${y}`;
    }

    function emirateText() {
      const sel = document.querySelector('#Emirate');
      if (!sel) return '—';
      const opt = sel.options[sel.selectedIndex];
      return opt ? opt.text.trim() : '—';
    }
    function currentDmy() {
      const input = document.querySelector('#datepicker');
      if (input?.value) return input.value;
      const hidden = document.querySelector('input[name="attributes[Delivery-Date]"]');
      if (hidden?.value) return hidden.value;
      return '';
    }
    function dateLabel(dmy) {
      if (dmy === dubaiDmy(0)) return 'Today';
      if (dmy === dubaiDmy(1)) return 'Tomorrow';
      return dmy ? dmy.replace(/-/g,'/') : '—';
    }
    function timeLabel() {
      const checked = document.querySelector('input[name="attributes[Delivery Time]"]:checked');
      if (!checked) return '—';
      const lbl = document.querySelector(`label[for="${checked.id}"]`);
      return lbl ? lbl.textContent.trim() : (checked.value || '—');
    }
    function print(prefix='') {
      const emir = emirateText();
      const dmy = currentDmy();
      const human = dmy.replace(/-/g, '/');
      const tag = dateLabel(dmy);
      const line = `Emirates: ${emir} -> ${tag} [${human}] -> ${timeLabel()}`;
      console.log(prefix ? `${prefix} ${line}` : line);
    }

    document.addEventListener('change', (e) => {
      if (e.target?.id === 'Emirate') setTimeout(() => print('[Emirate change]'), 250);
      if (e.target?.matches?.('input[name="attributes[Delivery Time]"]')) setTimeout(() => print('[Time change]'), 250);
      if (e.target?.id === 'datepicker') setTimeout(() => print('[Calendar change]'), 300);
    }, true);

    document.addEventListener('click', (e) => {
      const a = e.target.closest('.tab-a');
      if (!a) return;
      const id = a.getAttribute('data-id');
      const tag =
        id === 'tab1' ? '[Today click]' :
        id === 'tab2' ? '[Tomorrow click]' :
        '[Calendar tab click]';
      setTimeout(() => print(tag), 400);
    }, true);

    window.addEventListener('load', () => setTimeout(() => print('[Initial]'), 800));
  })();

  /* ==============================
     No Message Option (race-safe)
     ============================== */
  function setupNoMessageOption() {
    const aiContainer = document.querySelector('#aiCtrHere');
    if (!aiContainer) return;

    const noMessageLabel = document.createElement('label');
    noMessageLabel.className = 'hdr';
    noMessageLabel.htmlFor = 'no-message';
    noMessageLabel.style.cursor = 'pointer';
    noMessageLabel.style.fontWeight = 'normal';
    noMessageLabel.innerHTML = `<input type='checkbox' id='no-message' value="1"> No Message Card Needed`;

    aiContainer.prepend(noMessageLabel);
    setupNoMessageEventListener();
  }

  function setupNoMessageEventListener() {
    const noMessageCheckbox = document.querySelector('#no-message');
    if (!noMessageCheckbox) return;

    noMessageCheckbox.addEventListener('change', async (e) => {
      const cartNote = document.querySelector('#CartNote');
      const aiSuggestCheckbox = document.querySelector('#ai-suggest');

      if (window.__greetingSync?.cancel) window.__greetingSync.cancel();

      if (e.target.checked) {
        const aiForm = document.querySelector('#ai-form');
        if (aiForm) aiForm.style.display = 'none';

        if (aiSuggestCheckbox) aiSuggestCheckbox.checked = false;
        if (cartNote) {
          cartNote.value = 'No Message';
          cartNote.setAttribute('disabled', 'disabled');
          cartNote.style.display = 'block';
        }

        await updateAttrs({ 'Card Message': 'No Message' });
        console.log('[CartDelivery] No Message selected');
      } else {
        if (cartNote) {
          cartNote.value = '';
          cartNote.removeAttribute('disabled');
          cartNote.style.display = 'block';
        }

        await updateAttrs({ 'Card Message': '' });
        console.log('[CartDelivery] Message field re-enabled');
      }
    });
  }
})();
